import { ReactElement, useState, useCallback, useMemo } from 'react';
import { Typography, Stack, Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { defaultTo } from 'lodash';
import moment from 'moment';

import { LoadingFallback } from '~components/index';
import { useLazyRequest, useInfiniteScroll } from '~hooks/index';
import { searchRoutes } from '~services/route';
import { RouteFilter, SearchRoute, RouteStats } from '~services/route/types';
import {
  PaginationRequestParams,
  DataPaginateResponse,
  ErrorResponse,
} from '~globals/types';
import { RouteStateTypes } from '~globals/types/enums';
import { formatterDate } from '~utils/formatter';
import { toAbsoluteUrl } from '~utils/assetsHelpers';

import { AddOrdersToRouteMenuProps } from '../../types';
import AddOrdersToRouteMenuRouteCard from '../../RouteCard';
import {
  HEIGHT_ROUTE_CARD,
  AddOrdersToRouteMenuContent,
  AddOrdersToRouteMenuContentList,
} from '../../styles';

const defaultSizePerPage = 10;

const AddOrdersToRouteMenuInProgress = ({
  onClickCard,
}: Pick<AddOrdersToRouteMenuProps, 'onClickCard'>): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const [inProgressRoutes, setInProgressRoutes] = useState<SearchRoute<true>[]>(
    [],
  );
  const [hasNextPage, setHasNextPage] = useState(true);
  const [page, setPage] = useState(1);

  const [
    ,
    loadingFetchInProgressRoutes,
    errorFetchInProgressRoutes,
    executeFetchInProgressRoutes,
  ] = useLazyRequest<
    PaginationRequestParams<RouteFilter<true>>,
    DataPaginateResponse<SearchRoute<true>[], RouteStats>,
    ErrorResponse
  >({
    request: searchRoutes,
    withPostSuccess: (response) => {
      const {
        data: { pagination, results },
      } = response.data as DataPaginateResponse<
        SearchRoute<true>[],
        RouteStats
      >;

      const newInProgressRoutes = [...inProgressRoutes, ...results];
      setInProgressRoutes(newInProgressRoutes);

      const totalSize = defaultTo(pagination?.count, results.length);
      setHasNextPage(newInProgressRoutes.length < totalSize);

      setPage((prevPage) => prevPage + 1);
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
    },
  });

  const onLoadMore = useCallback(() => {
    const fromDate = new Date(
      moment().subtract(3, 'days').startOf('day').format(),
    );

    const toDate = new Date(moment().add(3, 'days').endOf('day').format());

    executeFetchInProgressRoutes({
      pagination: {
        page,
        sizePerPage: defaultSizePerPage,
      },
      filters: {
        dateFrom: formatterDate(fromDate, {
          format: moment.HTML5_FMT.DATETIME_LOCAL_MS,
        }),
        dateTo: formatterDate(toDate, {
          format: moment.HTML5_FMT.DATETIME_LOCAL_MS,
        }),
        routeCode: null,
        driversId: null,
        vehiclesId: null,
        routeStateTypesId: [RouteStateTypes.InProgress],
        includeApiTransaction: true,
      },
    });
  }, [executeFetchInProgressRoutes, page]);

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: loadingFetchInProgressRoutes,
    hasNextPage,
    onLoadMore,
    disabled: !!errorFetchInProgressRoutes,
    rootMargin: `0px 0px ${HEIGHT_ROUTE_CARD}px 0px`,
  });

  const showLoading = useMemo(
    () => loadingFetchInProgressRoutes || hasNextPage,
    [loadingFetchInProgressRoutes, hasNextPage],
  );

  const notInProgressRoutes = useMemo(
    () =>
      !loadingFetchInProgressRoutes && !hasNextPage && !inProgressRoutes.length,
    [loadingFetchInProgressRoutes, hasNextPage, inProgressRoutes],
  );

  return (
    <AddOrdersToRouteMenuContent>
      <AddOrdersToRouteMenuContentList ref={rootRef}>
        {inProgressRoutes.map((route) => (
          <AddOrdersToRouteMenuRouteCard
            key={`in-progress-route-${route.routeId}-vehicle${route.vehicleId}`}
            route={route}
            onClick={() => onClickCard(route)}
          />
        ))}

        {showLoading && <LoadingFallback size={50} />}

        {notInProgressRoutes && (
          <Stack spacing={1}>
            <Typography
              variant="h6"
              textAlign="center"
              my="auto"
              fontWeight="bold"
            >
              No se encontraron rutas
            </Typography>

            <Box
              component="img"
              src={toAbsoluteUrl('/icons/icon_empty_routes.png')}
              alt="Empty Routes Icon"
              sx={{ height: 80, objectFit: 'contain' }}
            />
          </Stack>
        )}

        <div ref={sentryRef} />
      </AddOrdersToRouteMenuContentList>
    </AddOrdersToRouteMenuContent>
  );
};

export default AddOrdersToRouteMenuInProgress;
